import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  AlertTriangle,
  ArrowRight,
  CheckCircle,
  Download,
  FileText,
  Loader,
  Shield,
  X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { logger, devLog, ProcessingError } from '@/utils/logger';
import { awsAPI } from '@/services/awsAPI';
import type { JobStatusResponse } from '@/services/awsAPI';
import { stripeAPI } from '@/services/stripeAPI';
import { accountServiceAPI } from '@/services/accountServiceAPI';
import { cognitoAuth } from '@/services/cognitoAuth';
import { calculateEstimatedCost } from '@/utils';
import type { ProcessingType } from '@/types';
import { resolveProcessingRate as getProcessingRate } from '@/types/batch';


interface AccountInviteStatus {
  invited: boolean;
  requiresConfirmation?: boolean;
  message?: string;
  error?: string;
}

interface ProcessingJobMeta {
  jobId: string;
  processingType: ProcessingType;
  processableRows: number;
  totalRows: number;
  estimatedCost: number;
  maxPossibleCost: number;
  companyName?: string;
  fileName?: string;
  aiOverview?: boolean;
}

interface PaymentMeta {
  paymentIntentId: string;
  clientSecret?: string;
  paymentData?: {
    email: string;
    firstName: string;
    lastName: string;
    companyName: string;
  };
  accountInviteStatus?: AccountInviteStatus | null;
}

interface NavigationState {
  job?: ProcessingJobMeta;
  paymentIntentId?: string;
  clientSecret?: string;
  paymentData?: PaymentMeta['paymentData'];
  accountInviteStatus?: AccountInviteStatus | null;
}

const TICKER_TONE_STYLES = {
  processing: {
    container: 'border-slate-200 bg-white',
    icon: 'bg-blue-100 text-blue-600',
  },
  success: {
    container: 'border-green-200 bg-green-50',
    icon: 'bg-green-100 text-green-600',
  },
  error: {
    container: 'border-red-200 bg-red-50',
    icon: 'bg-red-100 text-red-600',
  },
} as const;

interface JobSummary {
  totalRecords: number;
  successfulRecords: number;
  failedRecords: number;
  downloadUrl?: string;
  expiresAt?: string;
}

type ProcessedRecordStatus = 'successful' | 'failed' | string;

interface RecentRecordEntry {
  id: string;
  name: string;
  status: ProcessedRecordStatus;
  error?: string | null;
  completedAt?: string | null;
}

type TickerOverride = RecentRecordEntry;

const STEP_LABEL_LOOKUP: Record<string, string> = {
  pending: 'Queued for processing',
  initializing: 'Initializing pipelines',
  validating: 'Validating data',
  dispatching_batches: 'Dispatching batches',
  aggregating_results: 'Aggregating results',
  finalizing: 'Finalizing results',
  completed: 'Processing complete',
  failed: 'Processing failed',
};

const formatStepLabel = (status?: JobStatusResponse | null): string => {
  if (!status) {
    return 'Processing ongoing...';
  }

  const rawStep = status.currentStep?.toLowerCase();
  if (!rawStep) {
    return status.status === 'completed' ? 'Processing complete' : 'Processing ongoing...';
  }

  if (STEP_LABEL_LOOKUP[rawStep]) {
    return STEP_LABEL_LOOKUP[rawStep];
  }

  const batchProcessingMatch = rawStep.match(/^processing_batch_(\d+)$/);
  if (batchProcessingMatch) {
    const batchNumber = Number(batchProcessingMatch[1]);
    if (Number.isFinite(batchNumber)) {
      if (status.plannedTotalBatches && status.plannedTotalBatches > 0) {
        return `Processing batch ${batchNumber} of ${status.plannedTotalBatches}`;
      }
      return `Processing batch ${batchNumber}`;
    }
  }

  const generic = rawStep.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
  return generic || 'Processing ongoing...';
};

const PAYMENT_STORAGE_KEY = (jobId: string) => `collectability-payment-${jobId}`;

export const ProcessingPage: React.FC = () => {
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as NavigationState | undefined;

  const [jobMeta, setJobMeta] = useState<ProcessingJobMeta | null>(null);
  const [paymentMeta, setPaymentMeta] = useState<PaymentMeta | null>(null);
  const [accountInviteStatus, setAccountInviteStatus] = useState<AccountInviteStatus | null>(null);
  const [statusMessage, setStatusMessage] = useState<string>('Initializing processing...');
  const [error, setError] = useState<string>('');
  const [pollingEnabled, setPollingEnabled] = useState(true);
  const [jobSummary, setJobSummary] = useState<JobSummary | null>(null);
  const [hasCapturedPayment, setHasCapturedPayment] = useState(false);
  const [tickerOverride, setTickerOverride] = useState<TickerOverride | null>(null);
  const [recentRecords, setRecentRecords] = useState<RecentRecordEntry[]>([]);
  const [downloadError, setDownloadError] = useState<string>('');
  const [elapsedSeconds, setElapsedSeconds] = useState<number>(0);
  const [finalElapsedTime, setFinalElapsedTime] = useState<string | null>(null);
  const terminalHandledRef = useRef(false);
  const lastRecordRef = useRef<string | null>(null);
  const tickerTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const timerIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startTimeRef = useRef<number | null>(null);

  useEffect(() => {
    if (!jobId) {
      devLog.warn('No jobId in URL params, redirecting to batch landing');
      navigate('/batch', { replace: true });
      return;
    }
    devLog.info('ProcessingPage mounted with jobId:', jobId);

    const storedState = locationState ?? {};
    const storedSession = sessionStorage.getItem(PAYMENT_STORAGE_KEY(jobId));
    if (!storedState.job && storedSession) {
      try {
        const parsed = JSON.parse(storedSession) as { job: ProcessingJobMeta; paymentIntentId: string; clientSecret?: string; paymentData?: PaymentMeta['paymentData']; accountInviteStatus?: AccountInviteStatus | null };
        storedState.job = storedState.job ?? parsed.job;
        storedState.paymentIntentId = storedState.paymentIntentId ?? parsed.paymentIntentId;
        storedState.clientSecret = storedState.clientSecret ?? parsed.clientSecret;
        storedState.paymentData = storedState.paymentData ?? parsed.paymentData;
        storedState.accountInviteStatus = storedState.accountInviteStatus ?? parsed.accountInviteStatus;
      } catch (storageError) {
        logger.warn('Failed to parse session storage payment metadata', storageError);
      }
    }

    if (storedState.job) {
      setJobMeta(storedState.job);
    }

    if (storedState.paymentIntentId) {
      setPaymentMeta({
        paymentIntentId: storedState.paymentIntentId,
        clientSecret: storedState.clientSecret,
        paymentData: storedState.paymentData,
        accountInviteStatus: storedState.accountInviteStatus ?? null,
      });
    }

    if (storedState.accountInviteStatus) {
      setAccountInviteStatus(storedState.accountInviteStatus);
    }
  }, [jobId, locationState, navigate]);

  useEffect(() => {
    return () => {
      if (tickerTimeoutRef.current) {
        clearTimeout(tickerTimeoutRef.current);
      }
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
    };
  }, []);

  const { data: jobStatus, isLoading } = useQuery({
    queryKey: ['job-status', jobId],
    queryFn: async () => {
      if (!jobId) {
        throw new ProcessingError('Missing job identifier');
      }

      devLog.info('Polling job status for jobId:', jobId);
      const status = await awsAPI.getJobStatus(jobId);
      devLog.info('Job status response:', status);
      const summary = status.summary;
      if (!jobMeta && summary) {
        const aiOverview = locationState?.job?.aiOverview ?? false;

        setJobMeta((prev) => prev ?? {
          jobId,
          processingType: (locationState?.job?.processingType ?? 'validate') as ProcessingType,
          processableRows: summary.successfulRecords ?? locationState?.job?.processableRows ?? 0,
          totalRows: summary.totalRecords ?? locationState?.job?.totalRows ?? status.progress ?? 0,
          estimatedCost:
            locationState?.job?.estimatedCost ??
            calculateEstimatedCost(summary.successfulRecords ?? 0, (locationState?.job?.processingType ?? 'validate') as ProcessingType, {
              aiOverview,
            }),
          maxPossibleCost:
            locationState?.job?.maxPossibleCost ??
            calculateEstimatedCost(summary.totalRecords ?? 0, (locationState?.job?.processingType ?? 'validate') as ProcessingType, {
              aiOverview,
            }),
          companyName: locationState?.job?.companyName,
          fileName: locationState?.job?.fileName,
          aiOverview,
        });
      }
      return status;
    },
    enabled: !!jobId && pollingEnabled,
    refetchInterval: (query) => {
      const status = query.state.data as JobStatusResponse | undefined;
      if (!status) {
        return 10000; // 10 seconds for initial polling
      }
      // Stop polling when completed or failed, otherwise poll every 10 seconds
      return status.status === 'completed' || status.status === 'failed' ? false : 10000;
    },
    retry: (failureCount, error) => {
      // If 429 rate limit error, retry with exponential backoff
      const errorMessage = error instanceof Error ? error.message : String(error);
      if (errorMessage.includes('429') || errorMessage.includes('Too Many Requests')) {
        devLog.warn(`Rate limit hit, retry attempt ${failureCount + 1}/5`);
        return failureCount < 5; // Max 5 retries for rate limits
      }
      // For other errors, standard retry logic
      return failureCount < 3;
    },
    retryDelay: (attemptIndex) => {
      // Exponential backoff: 5s, 10s, 20s, 40s, 80s
      const delay = Math.min(1000 * Math.pow(2, attemptIndex + 2), 80000);
      devLog.info(`Retrying after ${delay}ms (attempt ${attemptIndex + 1})`);
      return delay;
    },
  });

  // Timer effect - starts when job is processing, stops when completed/failed
  useEffect(() => {
    if (!jobStatus) {
      return;
    }

    // Start timer when processing begins
    if (jobStatus.status === 'processing' && !timerIntervalRef.current) {
      startTimeRef.current = Date.now();
      timerIntervalRef.current = setInterval(() => {
        if (startTimeRef.current) {
          const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000);
          setElapsedSeconds(elapsed);
        }
      }, 1000);
    }

    // Stop timer and capture final time when completed or failed
    if ((jobStatus.status === 'completed' || jobStatus.status === 'failed') && timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;

      // Calculate final elapsed time
      if (startTimeRef.current) {
        const finalSeconds = Math.floor((Date.now() - startTimeRef.current) / 1000);
        const minutes = Math.floor(finalSeconds / 60);
        const seconds = finalSeconds % 60;
        const formattedTime = `${minutes}:${seconds.toString().padStart(2, '0')}`;
        setFinalElapsedTime(formattedTime);
        devLog.info('Timer stopped, final elapsed time:', formattedTime);
      }
    }

    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
    };
  }, [jobStatus?.status]);

  const finalizeJob = useCallback(
    async (status: JobStatusResponse) => {
      if (!jobId) {
        devLog.warn('finalizeJob called without jobId');
        return;
      }

      devLog.info('Starting finalizeJob with status:', status);

      try {
        const summaryResponse = await awsAPI.getJobSummary(jobId).catch((summaryError) => {
          logger.warn('Failed to retrieve dedicated job summary. Falling back to status payload.', summaryError);
          devLog.warn('getJobSummary failed:', summaryError);
          return null;
        });

        // Use backend data only - NO FALLBACKS
        // First try dedicated summary response, then fall back to status summary
        const summaryData = summaryResponse || status.summary;

        if (!summaryData) {
          devLog.error('No summary data available for completed job');
          throw new Error('Backend returned completed status without summary data');
        }

        const summary: JobSummary = {
          totalRecords: summaryData.totalRecords,
          successfulRecords: summaryData.successfulRecords,
          failedRecords: summaryData.failedRecords,
          downloadUrl: summaryData.downloadUrl,
          expiresAt: summaryResponse?.expiresAt,
        };

        devLog.info('Job summary calculated:', {
          summary,
          summaryResponse,
          statusSummary: status.summary,
          statusBilling: status.billing,
          jobMeta,
        });

        setJobSummary(summary);

        const meta = jobMeta;
        const payment = paymentMeta;

        devLog.info('Finalizing job with metadata:', {
          hasMeta: !!meta,
          hasPayment: !!payment,
          paymentIntentId: payment?.paymentIntentId,
          meta: meta,
          payment: payment,
        });

        if (!meta || !payment?.paymentIntentId) {
          devLog.error('Missing metadata for job finalization:', {
            meta,
            payment,
            paymentIntentId: payment?.paymentIntentId,
          });
          setError('Processing completed, but payment metadata is missing. Please contact support.');
          return;
        }

        const rate = getProcessingRate(meta.processingType, summary.successfulRecords, meta.aiOverview ?? false);
        const actualAmount = Number((summary.successfulRecords * rate).toFixed(2));

        const shouldCapturePayment = actualAmount > 0;

        if (shouldCapturePayment) {
          devLog.info('Capturing payment:', {
            paymentIntentId: payment.paymentIntentId,
            actualAmount,
            successfulRows: summary.successfulRecords,
            failedRows: summary.failedRecords,
            rate,
          });

          // Retry payment capture with exponential backoff
          let captureAttempt = 0;
          const maxRetries = 3;
          let captureSuccess = false;

          while (captureAttempt < maxRetries && !captureSuccess) {
            try {
              await stripeAPI.capturePayment({
                paymentIntentId: payment.paymentIntentId,
                actualAmount,
                successfulRows: summary.successfulRecords,
                failedRows: summary.failedRecords,
              });
              captureSuccess = true;
              devLog.info('Payment captured successfully, navigating to results');
            } catch (captureError) {
              captureAttempt++;
              const isLastAttempt = captureAttempt >= maxRetries;

              logger.error(`Payment capture attempt ${captureAttempt}/${maxRetries} failed:`, captureError);

              if (isLastAttempt) {
                // Final attempt failed - log critical error and continue to results
                // The payment authorization will remain in place for manual reconciliation
                logger.error('CRITICAL: Payment capture failed after all retries. Manual reconciliation required.', {
                  paymentIntentId: payment.paymentIntentId,
                  jobId,
                  actualAmount,
                  successfulRows: summary.successfulRecords,
                  error: captureError instanceof Error ? captureError.message : String(captureError),
                });

                // Alert user but don't block navigation to results
                setError(
                  'Processing completed but payment capture failed. Your card has not been charged. Please contact support with job ID: ' +
                    jobId
                );

                // Break out of retry loop
                break;
              } else {
                // Exponential backoff: 1s, 2s, 4s
                const delayMs = Math.pow(2, captureAttempt - 1) * 1000;
                devLog.warn(`Retrying payment capture in ${delayMs}ms...`, {
                  attempt: captureAttempt,
                  maxRetries,
                  paymentIntentId: payment.paymentIntentId,
                });
                await new Promise((resolve) => setTimeout(resolve, delayMs));
              }
            }
          }
        } else {
          devLog.warn('Skipping payment capture because actual amount is zero, cancelling authorization instead', {
            paymentIntentId: payment.paymentIntentId,
            successfulRows: summary.successfulRecords,
            failedRows: summary.failedRecords,
            rate,
          });

          await stripeAPI.cancelPayment({
            paymentIntentId: payment.paymentIntentId,
            reason: summary.successfulRecords === 0 ? 'no_successful_rows' : 'zero_actual_amount',
          });

          devLog.info('Payment authorization cancelled, navigating to results');
        }

        setHasCapturedPayment(true);
        sessionStorage.removeItem(PAYMENT_STORAGE_KEY(jobId));

        // Save batch history record to DynamoDB (non-blocking)
        try {
          const currentUser = cognitoAuth.getCurrentUser();
          if (currentUser?.userId && payment.paymentData?.email) {
            logger.info('Saving batch history to DynamoDB', {
              jobId,
              cognitoUserId: currentUser.userId,
              email: payment.paymentData.email
            });

            // Don't await - save in background, don't block navigation
            accountServiceAPI.saveBatchHistory({
              cognitoUserId: currentUser.userId,
              email: payment.paymentData.email,
              jobId,
              filename: meta.fileName || 'unknown.csv',
              processingType: meta.processingType,
              totalRecords: summary.totalRecords,
              successfulRecords: summary.successfulRecords,
              failedRecords: summary.failedRecords,
              cost: actualAmount,
              downloadUrl: summary.downloadUrl,
              expiresAt: summary.expiresAt,
              companyName: payment.paymentData.companyName || meta.companyName,
            }).catch((saveError) => {
              // Log but don't block user flow
              logger.error('Failed to save batch history (non-blocking):', saveError);
            });
          } else {
            logger.warn('Cannot save batch history - missing cognitoUserId or email', {
              hasUserId: !!currentUser?.userId,
              hasEmail: !!payment.paymentData?.email
            });
          }
        } catch (historyError) {
          // Log but don't block user flow
          logger.error('Error saving batch history (non-blocking):', historyError);
        }

        // Calculate elapsed time at navigation time (to avoid React state timing issues)
        let calculatedElapsedTime: string | null = null;
        if (startTimeRef.current) {
          const finalSeconds = Math.floor((Date.now() - startTimeRef.current) / 1000);
          const minutes = Math.floor(finalSeconds / 60);
          const seconds = finalSeconds % 60;
          calculatedElapsedTime = `${minutes}:${seconds.toString().padStart(2, '0')}`;
        }

        devLog.info('Navigating to results page with elapsedTime:', calculatedElapsedTime);

        navigate(`/batch/results/${jobId}`, {
          replace: true,
          state: {
            jobId,
            email: payment.paymentData?.email,
            customerName: payment.paymentData
              ? `${payment.paymentData.firstName} ${payment.paymentData.lastName}`.trim()
              : undefined,
            companyName: payment.paymentData?.companyName ?? meta.companyName ?? 'CollectABILITY Customer',
            processingType: meta.processingType,
            results: {
              successfulRows: summary.successfulRecords,
              failedRows: summary.failedRecords,
              totalProcessed: summary.totalRecords,
              downloadUrl: summary.downloadUrl,
            },
            actualCost: actualAmount,
            estimatedCost: meta.estimatedCost,
            maxAuthorizedCost: meta.maxPossibleCost,
            fileName: meta.fileName,
            paymentIntentId: payment.paymentIntentId,
            accountInviteStatus: payment.accountInviteStatus ?? accountInviteStatus ?? null,
            elapsedTime: calculatedElapsedTime,
          },
        });
      } catch (finalizeError) {
        logger.error('Failed to finalize processing workflow', finalizeError);
        devLog.error('finalizeJob error details:', {
          error: finalizeError,
          errorMessage: finalizeError instanceof Error ? finalizeError.message : 'Unknown error',
          stack: finalizeError instanceof Error ? finalizeError.stack : undefined,
        });
        setError(
          finalizeError instanceof Error
            ? finalizeError.message
            : 'Processing completed but we were unable to finalize billing. Please contact support.',
        );
      }
    },
    [jobId, jobMeta, navigate, paymentMeta, accountInviteStatus],
  );

  const handleFailure = useCallback(
    async (status: JobStatusResponse) => {
      if (!jobId) {
        return;
      }

      setError(status.error ?? 'Processing failed. You have not been charged.');

      const paymentIntentId = paymentMeta?.paymentIntentId;
      if (paymentIntentId) {
        try {
          await stripeAPI.cancelPayment({
            paymentIntentId,
            reason: status.error ?? 'Batch processing failed',
          });
          sessionStorage.removeItem(PAYMENT_STORAGE_KEY(jobId));
        } catch (cancelError) {
          logger.error('Failed to cancel payment intent after job failure', cancelError);
        }
      }
    },
    [jobId, paymentMeta],
  );

  // Monitor job status and trigger finalization when terminal state reached
  useEffect(() => {
    if (!jobStatus) {
      setStatusMessage('Processing ongoing...');
      return;
    }
    devLog.info('Job status update received:', {
      status: jobStatus.status,
      terminalHandled: terminalHandledRef.current,
      summary: jobStatus.summary,
      billing: jobStatus.billing,
    });

    setStatusMessage(formatStepLabel(jobStatus));

    if (!terminalHandledRef.current && (jobStatus.status === 'completed' || jobStatus.status === 'failed')) {
      devLog.info('Terminal status detected, handling completion/failure');
      terminalHandledRef.current = true;
      setPollingEnabled(false);

      if (jobStatus.status === 'completed') {
        devLog.info('Calling finalizeJob for completed job');
        void finalizeJob(jobStatus);
      } else if (jobStatus.status === 'failed') {
        devLog.info('Calling handleFailure for failed job');
        void handleFailure(jobStatus);
      }
    }
  }, [jobStatus, finalizeJob, handleFailure]);

  useEffect(() => {
    if (!jobStatus) {
      return;
    }

    const lastRecord = jobStatus.lastProcessedRecord;
    if (lastRecord) {
      const totalProcessedFallback =
        (typeof jobStatus.processedRecords === 'number' ? jobStatus.processedRecords : null) ??
        (jobStatus.successfulRecords + jobStatus.failedRecords);
      const derivedIndexCandidate =
        typeof lastRecord.recordIndex === 'number' && Number.isFinite(lastRecord.recordIndex)
          ? lastRecord.recordIndex
          : totalProcessedFallback > 0
            ? totalProcessedFallback - 1
            : 0;
      const normalizedIndex = Number.isFinite(derivedIndexCandidate) ? derivedIndexCandidate : 0;
      const key = `${normalizedIndex}-${lastRecord.status ?? 'unknown'}-${lastRecord.completedAt ?? ''}`;

      if (lastRecordRef.current !== key) {
        lastRecordRef.current = key;

        const displayName = (lastRecord.name ?? '').trim() || `Record #${normalizedIndex + 1}`;
        const entry: RecentRecordEntry = {
          id: key,
          name: displayName,
          status: lastRecord.status ?? 'completed',
          error: lastRecord.error,
          completedAt: lastRecord.completedAt ?? new Date().toISOString(),
        };

        setRecentRecords((prev) => {
          const deduped = prev.filter((item) => item.id !== key);
          return [entry, ...deduped].slice(0, 5);
        });

        setTickerOverride(entry);
        if (tickerTimeoutRef.current) {
          clearTimeout(tickerTimeoutRef.current);
        }
        tickerTimeoutRef.current = setTimeout(() => {
          setTickerOverride(null);
          tickerTimeoutRef.current = null;
        }, 3000);
      }
    }

    if (jobStatus.status === 'completed' || jobStatus.status === 'failed') {
      if (tickerTimeoutRef.current) {
        clearTimeout(tickerTimeoutRef.current);
        tickerTimeoutRef.current = null;
      }
      setTickerOverride(null);
    }
  }, [jobStatus]);

  const formatTimestamp = useCallback((iso?: string | null) => {
    if (!iso) {
      return 'Just now';
    }
    const parsed = new Date(iso);
    if (Number.isNaN(parsed.getTime())) {
      return 'Just now';
    }
    return parsed.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  }, []);

  const handleCancelProcessing = async () => {
    if (!jobId) {
      return;
    }

    try {
      await awsAPI.cancelJob(jobId);
      const paymentIntentId = paymentMeta?.paymentIntentId;
      if (paymentIntentId) {
        await stripeAPI.cancelPayment({ paymentIntentId, reason: 'Customer cancelled job' });
        sessionStorage.removeItem(PAYMENT_STORAGE_KEY(jobId));
      }
      navigate('/batch', { replace: true });
    } catch (cancelError) {
      logger.error('Failed to cancel processing', cancelError);
      setError(cancelError instanceof Error ? cancelError.message : 'Unable to cancel processing at this time.');
    }
  };

  const handleDownloadClick = useCallback(
    async (format: 'csv' | 'json') => {
      if (!jobId) {
        return;
      }
      setDownloadError('');
      try {
        const response = await awsAPI.getDownloadUrl(jobId, { format });
        if (response.downloadUrl) {
          if (typeof window !== 'undefined') {
            window.open(response.downloadUrl, '_blank', 'noopener,noreferrer');
          }
        } else {
          setDownloadError(`Download URL for ${format.toUpperCase()} is not available yet.`);
        }
      } catch (downloadErr) {
        logger.error(`Failed to generate ${format.toUpperCase()} download`, downloadErr);
        setDownloadError(
          downloadErr instanceof Error
            ? downloadErr.message
            : `Unable to download ${format.toUpperCase()} results right now.`,
        );
      }
    },
    [jobId],
  );

  const progressValue = useMemo(() => {
    if (!jobStatus) {
      return 0;
    }
    if (jobStatus.status === 'completed') {
      return 100;
    }
    if (jobStatus.status === 'failed') {
      return 0;
    }
    // Backend guarantees progress is a number 0-100
    return Math.min(100, Math.max(0, jobStatus.progress));
  }, [jobStatus]);

  const batchDispatchSummary = useMemo(() => {
    if (!jobStatus?.plannedTotalBatches) {
      return '';
    }
    const totalBatches = jobStatus.plannedTotalBatches;
    const dispatched = Math.min(jobStatus.dispatchedBatches ?? 0, totalBatches);

    if (jobStatus.status === 'completed') {
      return `Batches processed: ${totalBatches} of ${totalBatches}`;
    }

    if (dispatched === 0) {
      return `Preparing ${totalBatches} batch${totalBatches === 1 ? '' : 'es'}`;
    }

    return `Batches dispatched: ${dispatched} of ${totalBatches}`;
  }, [jobStatus]);

  const statusLabel = useMemo(() => {
    if (!jobStatus) {
      return 'Pending';
    }
    switch (jobStatus.status) {
      case 'completed':
        return 'Completed';
      case 'failed':
        return 'Failed';
      case 'processing':
        return 'Processing';
      default:
        return 'Pending';
    }
  }, [jobStatus]);

  const totalRecords = jobSummary?.totalRecords ?? jobStatus?.totalRecords ?? jobMeta?.totalRows ?? 0;
  const successfulRecords = jobSummary?.successfulRecords ?? jobStatus?.successfulRecords ?? 0;
  const failedRecords = jobSummary?.failedRecords ?? jobStatus?.failedRecords ?? 0;

  const processedRecordsCount = jobStatus?.processedRecords ?? Math.max(successfulRecords + failedRecords, 0);

  const lastFailureEntry = useMemo(
    () => recentRecords.find((entry) => entry.status === 'failed'),
    [recentRecords],
  );

  const progressCountsLabel = useMemo(() => {
    if (totalRecords > 0) {
      return `${processedRecordsCount.toLocaleString()} / ${totalRecords.toLocaleString()} processed`;
    }
    return `${processedRecordsCount.toLocaleString()} processed`;
  }, [processedRecordsCount, totalRecords]);

  const formattedElapsedTime = useMemo(() => {
    // If job is completed/failed, show final time
    if (finalElapsedTime) {
      return finalElapsedTime;
    }
    // Otherwise show live elapsed time
    const minutes = Math.floor(elapsedSeconds / 60);
    const seconds = elapsedSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }, [elapsedSeconds, finalElapsedTime]);

  const lastProgressDisplay = useMemo(() => {
    if (!jobStatus?.lastProgressEventAt) {
      return '';
    }
    const timestamp = new Date(jobStatus.lastProgressEventAt);
    if (Number.isNaN(timestamp.getTime())) {
      return '';
    }
    return `Last update ${timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}`;
  }, [jobStatus?.lastProgressEventAt]);

  const tickerContent = useMemo(() => {
    if (!jobStatus) {
      return {
        tone: 'processing' as const,
        label: 'Connecting to worker...',
        detail: 'Waiting for the first progress event.',
        icon: <Loader className="h-4 w-4 animate-spin text-blue-600" />,
      };
    }

    if (jobStatus.status === 'completed') {
      const detail =
        totalRecords > 0
          ? `${successfulRecords.toLocaleString()} succeeded | ${failedRecords.toLocaleString()} failed`
          : 'Results are ready to download.';
      return {
        tone: 'success' as const,
        label: 'Batch complete',
        detail,
        icon: <CheckCircle className="h-4 w-4 text-green-600" />,
      };
    }

    if (jobStatus.status === 'failed') {
      return {
        tone: 'error' as const,
        label: 'Processing halted',
        detail: lastFailureEntry?.error ?? 'The worker stopped after an unrecoverable error.',
        icon: <AlertTriangle className="h-4 w-4 text-red-500" />,
      };
    }

    if (tickerOverride) {
      const normalizedStatus =
        tickerOverride.status === 'failed'
          ? 'Failed'
          : tickerOverride.status === 'successful'
            ? 'Successful'
            : (tickerOverride.status ?? 'Completed');

      return {
        tone: tickerOverride.status === 'failed' ? ('error' as const) : ('success' as const),
        label: `Completed: ${tickerOverride.name}`,
        detail:
          tickerOverride.status === 'failed'
            ? tickerOverride.error ?? 'Record failed to process.'
            : `Status: ${normalizedStatus}`,
        icon:
          tickerOverride.status === 'failed' ? (
            <AlertTriangle className="h-4 w-4 text-red-500" />
          ) : (
            <CheckCircle className="h-4 w-4 text-green-600" />
          ),
      };
    }

    const rawName = (jobStatus.currentRecordName ?? '').trim();
    const recordIndex = typeof jobStatus.currentRecordIndex === 'number' ? jobStatus.currentRecordIndex + 1 : null;
    const labelName = rawName || (recordIndex ? `Record #${recordIndex}` : 'next record');
    const batchDetail =
      typeof jobStatus.currentRecordBatch === 'number' && typeof jobStatus.currentBatchSize === 'number'
        ? `Batch ${jobStatus.currentRecordBatch}, record #${recordIndex ?? '?'} of ${jobStatus.currentBatchSize}`
        : recordIndex
          ? `Processing record #${recordIndex}`
          : 'Worker is processing records...';

    return {
      tone: 'processing' as const,
      label: `Processing: ${labelName}`,
      detail: batchDetail,
      icon: <Loader className="h-4 w-4 animate-spin text-blue-600" />,
    };
  }, [failedRecords, jobStatus, successfulRecords, tickerOverride, totalRecords, lastFailureEntry]);

  const tickerToneStyle = TICKER_TONE_STYLES[tickerContent.tone] ?? TICKER_TONE_STYLES.processing;

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-3">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Processing Batch #{jobId?.split('_').pop()}</span>
              <div className="flex items-center gap-4">
                {(jobStatus?.status === 'processing' || finalElapsedTime) && (
                  <span className="text-sm font-mono font-medium text-blue-600">
                    {formattedElapsedTime}
                  </span>
                )}
                <span className="text-sm font-normal text-slate-500">Status: {statusLabel}</span>
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <Progress
                value={progressValue}
                className="h-3"
                showShimmer={jobStatus?.status === 'processing'}
              />
              <div className="mt-2 flex flex-col gap-1 text-xs text-slate-500 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-slate-600">
                  <span>{statusMessage}</span>
                  {lastProgressDisplay && <span className="text-slate-400">| {lastProgressDisplay}</span>}
                  {(jobStatus?.status === 'processing' || finalElapsedTime) && (
                    <span className="text-slate-400">| Elapsed: {formattedElapsedTime}</span>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                  {batchDispatchSummary && <span>{batchDispatchSummary}</span>}
                  <span className="font-medium text-slate-600">{progressCountsLabel}</span>
                  <span>{progressValue}%</span>
                </div>
              </div>
            </div>

            <div className={`flex flex-col gap-2 rounded-lg border p-4 sm:flex-row sm:items-center sm:justify-between ${tickerToneStyle.container}`}>
              <div className="flex items-center gap-3">
                <div className={`flex h-9 w-9 items-center justify-center rounded-full ${tickerToneStyle.icon}`}>
                  {tickerContent.icon}
                </div>
                <div>
                  <div className="text-sm font-medium text-slate-900">{tickerContent.label}</div>
                  {tickerContent.detail && <div className="text-xs text-slate-600">{tickerContent.detail}</div>}
                </div>
              </div>
              <div className="text-xs text-slate-500">
                Processed {processedRecordsCount.toLocaleString()} record{processedRecordsCount === 1 ? '' : 's'}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className={`border rounded-lg p-4 text-center ${progressValue >= 10 ? 'bg-blue-50 border-blue-200' : 'border-slate-200'}`}>
                <div className={`w-10 h-10 rounded-full flex items-center justify-center mx-auto mb-2 ${progressValue >= 10 ? 'bg-blue-600 text-white' : 'bg-slate-200 text-slate-600'}`}>
                  <FileText className="w-5 h-5" />
                </div>
                <div className="font-medium text-slate-900">Data Validation</div>
                <div className="text-xs text-slate-600 mt-1">Checking file structure and required columns</div>
                {progressValue >= 30 && <CheckCircle className="w-4 h-4 text-green-600 mx-auto mt-2" />}
              </div>
              <div className={`border rounded-lg p-4 text-center ${progressValue >= 40 ? 'bg-blue-50 border-blue-200' : 'border-slate-200'}`}>
                <div className={`w-10 h-10 rounded-full flex items-center justify-center mx-auto mb-2 ${progressValue >= 40 ? 'bg-blue-600 text-white' : 'bg-slate-200 text-slate-600'}`}>
                  <Shield className="w-5 h-5" />
                </div>
                <div className="font-medium text-slate-900">Processing</div>
                <div className="text-xs text-slate-600 mt-1">Running selected intelligence pipelines</div>
                {progressValue >= 70 && <CheckCircle className="w-4 h-4 text-green-600 mx-auto mt-2" />}
              </div>
              <div className={`border rounded-lg p-4 text-center ${progressValue >= 90 ? 'bg-blue-50 border-blue-200' : 'border-slate-200'}`}>
                <div className={`w-10 h-10 rounded-full flex items-center justify-center mx-auto mb-2 ${progressValue >= 90 ? 'bg-blue-600 text-white' : 'bg-slate-200 text-slate-600'}`}>
                  <CheckCircle className="w-5 h-5" />
                </div>
                <div className="font-medium text-slate-900">Finalization</div>
                <div className="text-xs text-slate-600 mt-1">Reconciling counts and preparing download</div>
                {progressValue >= 100 && <CheckCircle className="w-4 h-4 text-green-600 mx-auto mt-2" />}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-slate-600">
              <div className="border border-slate-200 rounded-lg p-4">
                <div className="text-xs uppercase text-slate-500">Total Records</div>
                <div className="text-xl font-semibold text-slate-900">{totalRecords.toLocaleString()}</div>
              </div>
              <div className="border border-slate-200 rounded-lg p-4">
                <div className="text-xs uppercase text-slate-500">Successful</div>
                <div className="text-xl font-semibold text-green-600">{successfulRecords.toLocaleString()}</div>
              </div>
              <div className="border border-slate-200 rounded-lg p-4">
                <div className="text-xs uppercase text-slate-500">Failed</div>
                <div className="text-xl font-semibold text-red-500">{failedRecords.toLocaleString()}</div>
              </div>
            </div>

            {recentRecords.length > 0 && (
              <div className="border border-slate-200 rounded-lg bg-white p-4">
                <div className="text-xs uppercase text-slate-500 mb-3">Recent activity</div>
                <div className="space-y-3">
                  {recentRecords.map((entry) => (
                    <div key={entry.id} className="flex items-start gap-3">
                      <span
                        className={`mt-1 inline-flex h-2 w-2 rounded-full ${entry.status === 'failed' ? 'bg-red-500' : 'bg-green-500'}`}
                      />
                      <div className="space-y-1">
                        <div className="text-sm text-slate-700">
                          {entry.status === 'failed' ? 'Failed' : 'Completed'}: {entry.name}
                        </div>
                        <div className="text-xs text-slate-500">{formatTimestamp(entry.completedAt)}</div>
                        {entry.error && (
                          <div className="text-xs text-red-500">{entry.error}</div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex flex-wrap items-center gap-3">
              <Button variant="outline" onClick={handleCancelProcessing} disabled={jobStatus?.status === 'completed' || jobStatus?.status === 'failed'}>
                <X className="w-4 h-4 mr-2" />
                Cancel Processing
              </Button>
              {jobStatus?.status === 'completed' && (
                <>
                  <Button
                    onClick={() => void handleDownloadClick('csv')}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download CSV
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => void handleDownloadClick('json')}
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download JSON
                  </Button>
                </>
              )}
              {(jobStatus?.status === 'failed' || error) && (
                <Button onClick={() => navigate('/batch')} className="bg-blue-600 hover:bg-blue-700">
                  Start New Job
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              )}
              {jobStatus?.status === 'completed' && !hasCapturedPayment && (
                <span className="text-xs text-slate-500">Finalizing billing...</span>
              )}
            </div>
            {downloadError && (
              <div className="text-xs text-red-500">{downloadError}</div>
            )}
          </CardContent>
        </Card>

        {error && (
          <Card className="border-red-200 bg-red-50">
            <CardHeader>
              <CardTitle className="text-red-800 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5" />
                Processing Issue
              </CardTitle>
            </CardHeader>
            <CardContent className="text-sm text-red-700 space-y-4">
              <p>{error}</p>
              <div>
                <button
                  onClick={() => navigate('/batch')}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm"
                >
                  Upload New File
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </CardContent>
          </Card>
        )}

        {isLoading && !jobStatus && (
          <div className="flex items-center gap-3 text-slate-600 text-sm">
            <Loader className="w-4 h-4 animate-spin" />
            Waiting for processing to begin...
          </div>
        )}
      </div>
    </div>
  );
};

export default ProcessingPage;

